import api from './api';
import type { User, MenuItem, ApiResponse } from '../types';

// VSN+ Token Exchange response
export interface TokenExchangeResponse {
  token: string;
  expiresAt: string;
  // VSNP-721: absolute logout time. Pinned to original session-start +
  // operator-configured Session Timeout (default 480 min); preserved
  // across refreshes; absent for remember-me sessions.
  absoluteExpiresAt?: string;
  user: {
    id: string;
    email: string;
    displayName: string;
    userType: number;
    tenantId: string | null;
    tenantName: string | null;
    roles: string[];
    permissions: string[];
  };
}

// VSN+ session user from /auth/me
export interface VSNSessionUser {
  id: string;
  email: string;
  displayName: string;
  userType: number;
  tenantId: string | null;
  tenantName: string | null;
  roles: string[];
  permissions: string[];
  sessionId: string;
  issuedAt: string;
  expiresAt: string;
}

// Keycloak password policy (subset surfaced by the API)
export interface PasswordPolicy {
  raw?: string | null;
  minLength?: number;
  maxLength?: number;
  upperCase?: number;
  lowerCase?: number;
  digits?: number;
  specialChars?: number;
  notUsername?: boolean;
  notEmail?: boolean;
  passwordHistory?: number;
}

// Invitation details returned by GET /auth/validate-invite
export interface InvitationInfo {
  email: string;
  // 5 (ChannelAdmin) is invitable by a SuperAdmin only; the provisioning
  // endpoint refuses it from anyone below.
  userType: 1 | 2 | 3 | 5;
  tenantId: string | null;
  tenantName: string | null;
  roleId: string | null;
  roleName: string | null;
  expiresAt: string;
  passwordPolicy: PasswordPolicy;
  /**
   * VSNP-687: cross-role activation marker. When true, the recipient
   * already has an active row in the OTHER user table (sys_user ↔
   * vpn_users) for this email — they share one Keycloak account by
   * design. The accept-invite page should render a single Activate
   * button instead of the password-set form, and call
   * authService.activateCrossRole(token) on submit.
   */
  crossRoleActivation?: boolean;
}

// Native login response: either a session for admin users, or an "app user"
// marker for VPN-only users (no session token issued).
export type NativeLoginResponse =
  | {
      appUser: true;
      displayName: string;
    }
  | {
      appUser?: false;
      token: string;
      expiresAt: string;
      absoluteExpiresAt?: string;
      user: TokenExchangeResponse['user'];
    };

export const authService = {
  /**
   * Get current user information
   */
  getUserInfo: async (): Promise<User> => {
    // V2 uses /users/me instead of /user/info which 404s
    const response = await api.get('/users/me');

    // Support both V1 (result field) and V2 (data field or direct object) formats
    let userData: any = response;

    if (response && typeof response === 'object') {
      if ('result' in response) {
        userData = (response as any).result;
      } else if ('data' in response) {
        userData = (response as any).data;
      }
    }

    if (userData && typeof userData === 'object') {
      // Map userId to id if needed
      if ('userId' in userData && !('id' in userData)) {
        userData.id = userData.userId;
      }
      return userData as User;
    }

    throw new Error('Invalid user info response format');
  },


  /**
   * Get dynamic menu list based on user permissions
   */
  getMenuList: async (): Promise<MenuItem[]> => {
    const response: ApiResponse<MenuItem[]> = await api.get('/permission/getMenuList');
    return response.result;
  },

  // ==========================================
  // VSN+ Token Exchange Methods
  // ==========================================

  /**
   * Exchange Keycloak token for VSN+ session token (legacy path — no longer
   * used by the native login flow but kept for the Assume Identity stash
   * which occasionally rebuilds a session).
   */
  exchangeToken: async (keycloakToken: string): Promise<TokenExchangeResponse> => {
    const response = await api.post('/auth/exchange', { keycloak_token: keycloakToken });
    return response.data as TokenExchangeResponse;
  },

  /**
   * Refresh VSN+ session token
   * Only works if current token is within refresh threshold
   */
  refreshVSNToken: async (): Promise<TokenExchangeResponse> => {
    const response = await api.post('/auth/refresh');
    return response.data as TokenExchangeResponse;
  },

  /**
   * Logout from VSN+ (revoke session)
   */
  logoutVSN: async (): Promise<void> => {
    await api.post('/auth/logout');
  },

  /**
   * Get current user from VSN+ session token
   */
  getVSNUser: async (): Promise<VSNSessionUser> => {
    const response = await api.get('/auth/me');
    return response.data as VSNSessionUser;
  },

  /**
   * Validate VSN+ session token
   */
  validateVSNToken: async (): Promise<{ valid: boolean; expiresAt: string; userId: string; userType: number }> => {
    const response = await api.get('/auth/validate');
    return response.data;
  },

  // ==========================================
  // Identity Assume Methods
  // ==========================================

  /**
   * Check if identity assume feature is enabled for the current user
   */
  checkAssumeEnabled: async (): Promise<{ enabled: boolean }> => {
    const response = await api.get('/auth/assume/check');
    return response.data as { enabled: boolean };
  },

  /**
   * Assume identity of a TenantAdmin user (SuperAdmin only)
   */
  assumeIdentity: async (userId: string): Promise<TokenExchangeResponse> => {
    const response = await api.post('/auth/assume', { userId });
    return response.data as TokenExchangeResponse;
  },

  // ==========================================
  // Native Auth Flow (VSNP-653)
  // ==========================================

  /**
   * Native login — email + password against Keycloak.
   * Returns either a VSN+ session (admin user) or an appUser marker (VPN user).
   */
  nativeLogin: async (
    email: string,
    password: string,
    rememberMe?: boolean
  ): Promise<NativeLoginResponse> => {
    const response = await api.post('/auth/login', { email, password, rememberMe });
    return response.data as NativeLoginResponse;
  },

  /**
   * Get the Keycloak realm password policy (public).
   * Used by /accept-invite and /profile/settings to show live validation hints.
   */
  getPasswordPolicy: async (): Promise<PasswordPolicy> => {
    const response = await api.get('/auth/password-policy');
    return response.data as PasswordPolicy;
  },

  /**
   * Validate an invitation token and return the shape the accept-invite page needs.
   */
  validateInvite: async (token: string): Promise<InvitationInfo> => {
    const response = await api.get('/auth/validate-invite', { params: { token } });
    return response.data as InvitationInfo;
  },

  /**
   * Accept an invitation — creates the KC user with the chosen password and
   * triggers verify-email. The caller must then sign in at /login.
   */
  acceptInvite: async (
    token: string,
    firstName: string,
    lastName: string,
    password: string
  ): Promise<{ email: string; emailVerificationRequired: boolean; message: string }> => {
    const response = await api.post('/auth/accept-invite', { token, firstName, lastName, password });
    return response.data;
  },

  /**
   * VSNP-687: cross-role activation. The invitee already has an account
   * in the OTHER role's table (sys_user ↔ vpn_users); this endpoint
   * grants the new role on the existing Keycloak account without a
   * password reset.
   */
  activateCrossRole: async (token: string): Promise<{ email: string; message: string }> => {
    const response = await api.post('/auth/activate-cross-role', { token });
    return response.data;
  },

  /**
   * Re-send the verify-email action email. Backend always returns 200 to avoid
   * leaking account existence, so the UI should show a generic confirmation.
   */
  resendVerification: async (email: string): Promise<{ message: string }> => {
    const response = await api.post('/auth/resend-verification', { email });
    return response.data;
  },

  /**
   * Consume an email-verify token (clicked from the verification email link).
   * Backend validates the token, flips KC emailVerified=true, marks token used.
   */
  verifyEmail: async (token: string): Promise<{ email: string; message: string }> => {
    const response = await api.post('/auth/verify-email', { token });
    return response.data;
  },

  /**
   * Pre-flight check on a password-reset token so the reset page can decide
   * whether to render the form or show an "expired link" screen. Does NOT
   * consume the token.
   */
  validateResetToken: async (token: string): Promise<{ email: string; expiresAt: string; passwordPolicy: PasswordPolicy }> => {
    const response = await api.get('/auth/validate-reset-token', { params: { token } });
    return response.data;
  },

  /**
   * Consume a password-reset token and set the new password via KC Admin API.
   * Server revokes all KC sessions on success so any stolen session stops working.
   */
  resetPassword: async (token: string, password: string): Promise<{ email: string; message: string }> => {
    const response = await api.post('/auth/reset-password', { token, password });
    return response.data;
  },
};

